import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Checkbox,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import FlexBox from "../../components/layouts/flexbox";
import { SimpleTable } from "../../components/mui/table";
import { CONST_PROJECT_STATUS_LIST } from "../../constants/lists";
import { RENDERER_TYPES } from "../../enums";
import { ROUTE_PATHS } from "../../routes/route-paths";
import { DATE_FORMATS, formatDate } from "../../services/date-and-time.utils";
import { convertToFullName, convertToUrl } from "../../services/utils";
import { ProjectStatus } from "../invitations/components/project-status";

function ActivityLogVisitedCheckbox({ row, onVisitedChange }) {
  const [isVisited, setIsVisited] = useState(() => Boolean(row?.isVisited));

  useEffect(() => {
    setIsVisited(Boolean(row?.isVisited));
  }, [row?._id, row?.isVisited]);

  const visitedTitle = isVisited ? "Marked as visited" : "Mark as visited";

  const handleChange = async (e) => {
    const nextVisited = e.target.checked;
    const previousVisited = isVisited;
    setIsVisited(nextVisited);

    const success = await onVisitedChange?.(row?._id, nextVisited);
    if (success === false) {
      setIsVisited(previousVisited);
    }
  };

  return (
    <Checkbox
      checked={isVisited}
      onChange={handleChange}
      disabled={!row?._id || !onVisitedChange}
      size="small"
      sx={{
        p: 0,
        color: "#9CA3AF",
        "&.Mui-checked": { color: "#3B82F6" },
      }}
      inputProps={{
        "aria-label": visitedTitle,
        title: visitedTitle,
      }}
    />
  );
}

function ActivityDetailRow({ label, children }) {
  return (
    <FlexBox
      flexDirection={{ xs: "column", md: "row" }}
      gap={{ xs: 0.25, md: 1 }}
      alignItems="flex-start"
    >
      <Typography
        fontSize={{ xs: 11, lg: 12 }}
        fontWeight="600"
        color="#9CA3AF"
        minWidth={{ md: 92 }}
        flexShrink={0}
      >
        {label}
      </Typography>
      <Box flex={1} minWidth={0}>
        {children}
      </Box>
    </FlexBox>
  );
}

const ActivityDetails = React.memo<{ row: any }>(function ActivityDetails({ row }) {
  const statusInfo = CONST_PROJECT_STATUS_LIST[row?.invitationStatus];
  const statusAccentColor = statusInfo?.fontColor || "#6B7280";
  const ambassadorName = convertToFullName(
    row?.ambassador?.firstName,
    row?.ambassador?.lastName
  );
  const ambassadorCompanyName = row?.ambassador?.company;
  const ambassadorEmail = row?.ambassador?.email;

  const clientName = convertToFullName(
    row?.client?.firstName,
    row?.client?.lastName
  );
  const clientCompanyName = row?.client?.company;
  const clientEmail = row?.client?.email;
  const displayClientName = clientCompanyName || clientName || "N/A";

  return (
    <FlexBox
      gap={{ xs: 1, lg: 1.5 }}
      alignItems="stretch"
    >
      <Box
        width={{ xs: 3, lg: 4 }}
        minHeight={40}
        bgcolor={statusAccentColor}
        borderRadius={1}
        flexShrink={0}
      />
      <FlexBox flex={1} minWidth={0} py={{ xs: 0.5, lg: 0 }} flexDirection="column" gap={1}>
        <ActivityDetailRow label="Ambassador:">
          <UserDetailsTooltip
            title="Ambassador Details"
            name={ambassadorName}
            company={ambassadorCompanyName}
            email={ambassadorEmail}
          >
            <Typography
              fontSize={{ xs: 11, lg: 12 }}
              color="#6B7280"
              variant="body2"
              fontWeight={600}
              display="inline-block"
              sx={{
                cursor: "pointer",
                wordBreak: "break-word",
              }}
            >
              {ambassadorName || "N/A"}
            </Typography>
          </UserDetailsTooltip>
        </ActivityDetailRow>

        <ActivityDetailRow label="Client:">
          <UserDetailsTooltip
            title="Client Details"
            name={clientName}
            company={clientCompanyName}
            email={clientEmail}
          >
            <Typography
              fontSize={{ xs: 11, lg: 12 }}
              color="#6B7280"
              variant="body2"
              display="inline-block"
              sx={{ cursor: "pointer", wordBreak: "break-word" }}
            >
              {displayClientName}
            </Typography>
          </UserDetailsTooltip>
        </ActivityDetailRow>

        <ActivityDetailRow label="Status:">
          <ProjectStatus data={row} />
        </ActivityDetailRow>
      </FlexBox>
    </FlexBox>
  );
});

function ActivityLogLisTable({
  list,
  columns,
  handlePageFilters,
  selectedPage,
  selectedPageSize,
  onVisitedChange,
}) {
  const handleOpenModal = (row) => {
    const path = convertToUrl(ROUTE_PATHS.CLIENTS.DETAILS, {
      ":id": row?.client?._id,
      ":tab": "invitation_status",
    });

    window.location.href = `${path}?invitationId=${row?._id}`;
  };

  const renderRole = (row) => {
    if (row?.actionPerformBy?.email === row?.ambassador?.email) {
      return "Ambassador";
    } else if (row?.actionPerformBy?.email.includes("@execweb.com")) {
      return "Admin";
    } else {
      return "Client";
    }
  };

  const handleTDRenderer = (row, column) => {
    switch (column.rendererType) {
      case RENDERER_TYPES.ACTION:
        return (
          <IconButton onClick={() => handleOpenModal(row)} size="small">
            <OpenInNewIcon />
          </IconButton>
        );
      case RENDERER_TYPES.FULL_DATE:
        return (
          <Typography fontSize={13} fontWeight="400" color="#6C7293">
            {formatDate(row[column.field], DATE_FORMATS.fullDateWithTime)}
          </Typography>
        );
      case "invitation_activity":
        return <ActivityDetails row={row} />;
      case "activity_log_visited":
        return (
          <ActivityLogVisitedCheckbox
            row={row}
            onVisitedChange={onVisitedChange}
          />
        );
      case "role":
        return (
          <Typography fontSize={13} fontWeight="400" color="#6C7293">
            {renderRole(row)}
          </Typography>
        );
      case RENDERER_TYPES.FULL_NAME:
        return (
          <Typography fontSize={13} fontWeight="400" color="#6C7293">
            {convertToFullName(
              row?.actionPerformBy?.firstName,
              row?.actionPerformBy?.lastName
            )}
          </Typography>
        );
      default:
        break;
    }
  };

  return (
    <SimpleTable
      tdRenderer={handleTDRenderer}
      rows={list?.data}
      columns={columns}
      fetching={list?.loading}
      onPageSettingsChange={handlePageFilters}
      total={list?.total}
      selectedPage={selectedPage}
      selectedPageSize={selectedPageSize}
    />
  );
}

// User Details Tooltip Component
function UserDetailsTooltip({ title, name, company = null, email, children }) {
  const tooltipContent = (
    <Box sx={{ p: 1, minWidth: 200 }}>
      <Typography
        fontSize={13}
        fontWeight="600"
        mb={1}
        color="#1F2937"
        borderBottom="1px solid #E5E7EB"
        pb={0.5}
      >
        {title}
      </Typography>
      <Grid container spacing={0.75}>
        {name && (
          <>
            <Grid item xs={4}>
              <Typography fontSize={12} color="#6B7280" fontWeight="600">
                Name:
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography fontSize={12} color="#1F2937">
                {name}
              </Typography>
            </Grid>
          </>
        )}
        {company && (
          <>
            <Grid item xs={4}>
              <Typography fontSize={12} color="#6B7280" fontWeight="600">
                Company:
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography fontSize={12} color="#1F2937">
                {company}
              </Typography>
            </Grid>
          </>
        )}
        {email && (
          <>
            <Grid item xs={4}>
              <Typography fontSize={12} color="#6B7280" fontWeight="600">
                Email:
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Typography fontSize={12} color="#1F2937">
                {email}
              </Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="right"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "white",
            color: "#1F2937",
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: "1px solid #E5E7EB",
            "& .MuiTooltip-arrow": {
              color: "white",
              "&:before": {
                border: "1px solid #E5E7EB",
              },
            },
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
}

export { ActivityLogLisTable };
